import React from 'react';

import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import DropdownMenu from '~/shared/components/DropdownMenu';
import NavLink from '~/shared/components/NavLink';
import getLocalizedPath from '~/shared/utils/getLocalizedPath';
import normalizeUrl from '~/shared/utils/normalizeUrl';
import useAccountLinks from '~/shared/utils/useAccountLinks';
import { desktop } from '~/techstyle-shared/react-components';
import { useDomain } from '~/techstyle-shared/redux-core';

import sanitizeWithUnderscore from '../../utils/tagHelper';

const Wrapper = styled.div`
  padding: 24px;
  ${desktop`display: none;`};

  [data-path-hierarchy='exact'],
  [data-path-hierarchy='within'] {
    color: ${({ theme }) => theme.colors.accountPageMenuMobile};
  }
`;

const DropdownMenuListItem = styled(DropdownMenu.ListItem)`
  &[aria-current='true'] {
    color: ${({ theme }) => theme.colors.textDefault};
  }
`;

export default function AccountPageMenuMobile({ title, ...rest }) {
  const router = useRouter();
  const { tld } = useDomain();
  const { data } = useAccountLinks({ isNavToolbar: false });

  const getNavItemHref = ({ url }) => {
    const path = getLocalizedPath({ path: url, tld });
    return path ? normalizeUrl(path) : undefined;
  };

  // TOOD: decide if this loading state should be part of our
  // DropdownMenu API or not
  const isLoading = Boolean(!data || !data.navItems);
  const navItems = !isLoading
    ? data.navItems.map((navItem) => {
        return {
          ...navItem,
          value: getNavItemHref(navItem),
        };
      })
    : [];

  // exclude links returned from the API which are not required
  const excludedForAllUsers = ['my_accounts_benefits', 'my_account_waitlist'];

  const navItemsForUserType = navItems.filter(
    (item) => !excludedForAllUsers.includes(item.itemKey)
  );

  const handleNavItemClick = (navItem) => {
    router.push(navItem.value);
  };

  return (
    <Wrapper {...rest}>
      <DropdownMenu options={navItemsForUserType} value="none">
        <DropdownMenu.Button
          showCaret={!isLoading}
          data-autotag={`topnav_${sanitizeWithUnderscore(title)}`}
        >
          {title}
        </DropdownMenu.Button>
        <DropdownMenu.List>
          {navItemsForUserType.map((navItem) => (
            <DropdownMenuListItem
              value={navItem.value}
              key={navItem.value}
              onClick={() => handleNavItemClick(navItem)}
            >
              <NavLink tagName="span" {...navItem} />
            </DropdownMenuListItem>
          ))}
        </DropdownMenu.List>
      </DropdownMenu>
    </Wrapper>
  );
}

AccountPageMenuMobile.propTypes = {
  title: PropTypes.string,
};
